import type { NamespacesPossibleKeys } from '@/lib/i18n/types'
import type { StatusDotProps } from '@/ui/status-dot/StatusDot'
import { Availability, ClickAndCollectStatus, ShippingDelay } from '@gql-generated/graphql'
import type { ProductStatusLineProps } from '../components/product-availabilities/ProductStatusLine.client'
import type { ProductAvailabilitiesProduct } from '../types/mergedProduct'

type OnlineItem = {
  id: string
  props: StrictOmit<ProductStatusLineProps, 'type'> & { type: 'online' }
}

type StoreItem = {
  id: string
  props: StrictOmit<ProductStatusLineProps, 'type'> & { type: 'store' }
}

function getStoreTints(
  storeQuantity: number,
): StrictPick<ProductStatusLineType, 'textTint' | 'statusDot'> {
  return {
    textTint: storeQuantity > 1 ? 'text-success-fg' : 'text-warning-fg',
    statusDot: {
      variant: storeQuantity > 1 ? 'success' : 'warning',
    },
  }
}

type StoreAvailabilityProps = StrictOmit<ProductStatusLineProps, 'type'>

export type ProductStatusLineType = {
  statusDot?: StatusDotProps
  i18nKey: NamespacesPossibleKeys<'productAvailabilities'>
  textTint?: string
  values?: Record<string, string | number | undefined>
}

function storeAvailability({
  availability,
  pointOfSaleName,
  shortMode,
}: StoreAvailabilityProps): ProductStatusLineType | undefined {
  const { availability: productAvailabilities, storeQuantity } = availability.stock
  const displayMode = shortMode ? 'short' : 'detailed'

  //Product Stopped
  if (productAvailabilities?.includes(Availability.StoppedInstore)) {
    return {
      i18nKey: 'productAvailabilities:store.stoppedInStore',
      values: {
        pointOfSaleName,
      },
      statusDot: {
        variant: 'unavailable',
      },
    }
  }

  //Product Out of stock
  if (storeQuantity === 0 || !storeQuantity) {
    return {
      i18nKey: `productAvailabilities:store.${pointOfSaleName ? 'storeZero' : 'localStock.storeZero'}`,
      values: {
        pointOfSaleName,
      },
      statusDot: {
        variant: 'error',
      },
    }
  }

  //Product In stock
  // Always uses classic "En stock, X unités en magasin" strings.
  // Click-and-collect–specific display (e.g. "X produits sur Y disponibles en C&C 2h")
  // is handled via customData: either by getStoreItems() for the BuyBox secondary C&C line,
  // or directly by ClickAndCollectSwitchBanner.
  if (storeQuantity > 0) {
    if (pointOfSaleName) {
      return {
        i18nKey: 'productAvailabilities:store.storeMany',
        values: {
          pointOfSaleName,
          storeQuantity,
          pluralCount: storeQuantity,
        },
        ...getStoreTints(storeQuantity),
      }
    } else {
      return {
        i18nKey: `productAvailabilities:store.localStock.storeMany.${displayMode}`,
        values: {
          storeQuantity,
          pluralCount: storeQuantity,
        },
        ...getStoreTints(storeQuantity),
      }
    }
  }
}

type OnlineAvailabilityProps = StrictOmit<ProductStatusLineProps, 'type'> & {
  maxQuantity?: number
}

// eslint-disable-next-line sonarjs/cognitive-complexity
function onlineAvailability({
  availability,
  cartQuantity,
  onlineQuantity,
  maxQuantity,
  shortMode,
}: OnlineAvailabilityProps): ProductStatusLineType | undefined {
  const {
    availability: productAvailabilities,
    shippingDelay,
    g9LowStockThreshold,
  } = availability.stock
  const displayMode = shortMode ? 'short' : 'detailed'

  if (!shippingDelay || productAvailabilities?.includes(Availability.StoppedOnline)) {
    return {
      i18nKey: 'productAvailabilities:online.noStock',
      statusDot: {
        variant: 'unavailable',
      },
    }
  }

  // When cartQuantity is provided, use it for the withQuantity display
  if (
    !maxQuantity &&
    cartQuantity &&
    (cartQuantity > 1 || shippingDelay === ShippingDelay.Weeks_4)
  ) {
    return {
      i18nKey: `productAvailabilities:online.shippingDelay.${shippingDelay}.withQuantity`,
      values: { cartQuantity, count: cartQuantity },
      textTint:
        shippingDelay === ShippingDelay.Weeks_4 ? 'text-click-and-collect-fg' : 'text-success-fg', // TODO: use text-danger
      statusDot: {
        variant: shippingDelay === ShippingDelay.Weeks_4 ? 'warning' : 'success',
      },
    }
  }

  // Limited stock
  if (
    maxQuantity &&
    (shippingDelay === ShippingDelay.Hours_24To_48 || shippingDelay === ShippingDelay.Days_3To_5)
  ) {
    if (g9LowStockThreshold && g9LowStockThreshold >= maxQuantity) {
      return {
        i18nKey: `productAvailabilities:online.limitedStockDelay.${shippingDelay}.${displayMode}`,
        values: { onlineQuantity, maxQuantity, count: maxQuantity },
        ...getStoreTints(maxQuantity),
      }
    }
  }

  if (shippingDelay) {
    if (shippingDelay == ShippingDelay.Hours_24To_48 || shippingDelay == ShippingDelay.Days_3To_5) {
      return {
        i18nKey: `productAvailabilities:online.shippingDelay.${shippingDelay}.${displayMode}`,
        textTint: 'text-success-fg',
        statusDot: {
          variant: 'success',
        },
      }
    } else {
      return {
        i18nKey:
          shippingDelay === ShippingDelay.Unknown
            ? `productAvailabilities:online.shippingDelay.UNKNOWN`
            : `productAvailabilities:online.shippingDelay.${shippingDelay}.${displayMode}`,
        statusDot: { variant: 'warning' },
        textTint: 'text-warning-fg',
      }
    }
  }
}

type GetProductAvailabilityProps = ProductStatusLineProps

export function getProductAvailabilityItem({
  availability,
  cartQuantity,
  onlineQuantity,
  maxQuantity,
  pointOfSaleName,
  shortMode,
  type,
  canAddToCart,
  clickAndCollectStatus,
}: GetProductAvailabilityProps) {
  if (type === 'online') {
    return onlineAvailability({
      availability,
      cartQuantity,
      onlineQuantity,
      maxQuantity,
      pointOfSaleName,
      shortMode,
      canAddToCart,
      clickAndCollectStatus,
    })
  } else {
    return storeAvailability({
      availability,
      cartQuantity,
      pointOfSaleName,
      shortMode,
      canAddToCart,
      clickAndCollectStatus,
    })
  }
}

export function getOnlineItems({
  product,
  selectedQuantity,
  isClarityDelayEnabled,
}: {
  product: ProductAvailabilitiesProduct
  selectedQuantity: number
  isClarityDelayEnabled: boolean
}): OnlineItem[] {
  // stock may be missing while the price/stock query is still loading
  if (!product.stock) return []

  const items: OnlineItem[] = []
  const availableQuantity = product.stock.onlineQuantity ?? 0

  if (isClarityDelayEnabled && product.stock.maxQuantity != null && availableQuantity > 0) {
    const firstQuantity = Math.min(selectedQuantity, availableQuantity)

    if (product.stock.maxQuantity && availableQuantity > 0) {
      items.push({
        id: `online-limited-stock-${firstQuantity}`,
        props: {
          availability: {
            ...product,
            stock: {
              ...product.stock,
              shippingDelay: product.stock.shippingDelay ?? ShippingDelay.Days_3To_5,
            },
          },
          type: 'online',
          onlineQuantity: product.stock.onlineQuantity ?? undefined,
          maxQuantity: product.stock.maxQuantity,
          cartQuantity: firstQuantity,
          canAddToCart: product.canAddToCart ?? false,
          clickAndCollectStatus: product.clickAndCollectStatus ?? null,
        },
      })
    } else {
      items.push({
        id: `online-in-stock-${firstQuantity}`,
        props: {
          availability: product,
          type: 'online',
          maxQuantity: product.stock.maxQuantity,
          cartQuantity: firstQuantity,
          canAddToCart: product.canAddToCart ?? false,
          clickAndCollectStatus: product.clickAndCollectStatus ?? null,
        },
      })
    }

    if (selectedQuantity > availableQuantity) {
      items.push({
        id: `online-delay-${selectedQuantity - availableQuantity}`,
        props: {
          availability: {
            ...product,
            stock: { ...product.stock, shippingDelay: ShippingDelay.Weeks_4 },
          },
          type: 'online',
          maxQuantity: product.stock.maxQuantity,
          cartQuantity: selectedQuantity - availableQuantity,
          canAddToCart: product.canAddToCart ?? false,
          clickAndCollectStatus: product.clickAndCollectStatus ?? null,
        },
      })
    }
  } else if (availableQuantity > 0) {
    if (selectedQuantity > availableQuantity) {
      items.push(
        {
          id: `online-in-stock-${availableQuantity}`,
          props: {
            availability: product,
            type: 'online',
            cartQuantity: availableQuantity,
            canAddToCart: product.canAddToCart ?? false,
            clickAndCollectStatus: product.clickAndCollectStatus ?? null,
          },
        },
        {
          id: `online-delay-${selectedQuantity - availableQuantity}`,
          props: {
            availability: {
              ...product,
              stock: { ...product.stock, shippingDelay: ShippingDelay.Weeks_4 },
            },
            type: 'online',
            cartQuantity: selectedQuantity - availableQuantity,
            canAddToCart: product.canAddToCart ?? false,
            clickAndCollectStatus: product.clickAndCollectStatus ?? null,
          },
        },
      )
    } else {
      items.push({
        id: `online-default`,
        props: {
          availability: product,
          type: 'online',
          canAddToCart: product.canAddToCart ?? false,
          clickAndCollectStatus: product.clickAndCollectStatus ?? null,
        },
      })
    }
  } else {
    items.push({
      id: `online-max-${product.stock.maxQuantity ?? 'none'}`,
      props: {
        availability: product,
        type: 'online',
        maxQuantity: product.stock.maxQuantity ?? undefined,
        canAddToCart: product.canAddToCart ?? false,
        clickAndCollectStatus: product.clickAndCollectStatus ?? null,
      },
    })
  }

  return items
}

export function getStoreItems({
  product,
  shortMode,
  isClickAndCollectEnabled,
  showAvailabilitiesCustomData = true,
  selectedQuantity,
}: {
  product: ProductAvailabilitiesProduct
  shortMode: boolean
  isClickAndCollectEnabled: boolean
  showAvailabilitiesCustomData?: boolean
  selectedQuantity?: number
}): StoreItem[] {
  // stock may be missing while the price/stock query is still loading
  if (!product.stock) return []

  const items: StoreItem[] = []

  items.push({
    id: 'store-default',
    props: {
      availability: product,
      type: 'store',
      cartQuantity: selectedQuantity,
      canAddToCart: product.canAddToCart ?? false,
      clickAndCollectStatus: product.clickAndCollectStatus ?? null,
    },
  })

  // Availabilities with customData: when in detailed mode (all contexts except references product table and product card
  // that use shortMode) and allowed, we append extra rows to highlight click-and-collect availability/express
  // status; the default store availability row stays.
  if (!shortMode && showAvailabilitiesCustomData) {
    const storeQuantity = product.stock.storeQuantity ?? 0

    const hasClickAndCollect =
      product.clickAndCollectStatus === ClickAndCollectStatus.Available ||
      product.clickAndCollectStatus === ClickAndCollectStatus.ClickAndCollectOnly

    const isStoreOnly = product.clickAndCollectStatus === ClickAndCollectStatus.StoreOnly

    const isOnlineStopped =
      !product.stock.shippingDelay ||
      !!product.stock.availability?.includes(Availability.StoppedOnline)

    const isClickAndCollectOnly =
      product.clickAndCollectStatus === ClickAndCollectStatus.ClickAndCollectOnly ||
      (hasClickAndCollect && isOnlineStopped)

    if (isClickAndCollectEnabled && (hasClickAndCollect || isStoreOnly) && storeQuantity > 0) {
      const i18nKey = isStoreOnly
        ? 'productAvailabilities:store.clickAndCollect.storeOnly'
        : isClickAndCollectOnly
          ? 'productAvailabilities:store.clickAndCollect.withDeliveryTimeOnly'
          : 'productAvailabilities:store.clickAndCollect.withDeliveryTime'

      items.push({
        id: 'store-click-collect',
        props: {
          availability: product,
          type: 'store',
          customData: {
            i18nKey,
            statusDot: { variant: 'success' },
          },
          canAddToCart: product.canAddToCart ?? false,
          clickAndCollectStatus: product.clickAndCollectStatus ?? null,
        },
      })
    }
  }

  return items
}
